import { lazy, Suspense, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { StateMessage } from '../components/StateMessage';
import { brandColor, teamUnavailable } from '../lib/display';
import { applyFilters, readFilters, writeFilters, type FilterState, type SortKey } from '../lib/filters';
import { usePortfolio } from '../lib/portfolio-context';
import type { Facility } from '../lib/types';

/**
 * The map carries d3-geo and topojson-client — about a third of the bundle for
 * a view most people never switch to. Loading it on demand keeps the table,
 * which is the default and the common case, cheap to open.
 */
const PortfolioMap = lazy(() =>
  import('../components/PortfolioMap').then((m) => ({ default: m.PortfolioMap })),
);

const COLS: { k: SortKey; t: string; w: string; cls?: string; num?: boolean }[] = [
  { k: 'code', t: 'Code', w: '82px' },
  { k: 'name', t: 'Facility', w: 'auto', cls: 'c-name' },
  { k: 'brand', t: 'Brand', w: 'auto', cls: 'c-brand' },
  // "District" named a place that does not exist in the source: the column
  // holds the district MANAGER's name (the repository binds it from
  // District/District_Manager, and the facility page renders the same value as
  // "District Manager"). The whole mirror was enumerated and there is no
  // district lookup in it, so there is nothing else this column could show —
  // the heading was the only thing wrong. The sort key stays `district`
  // because that is the field name, not the label.
  { k: 'district', t: 'District Manager', w: 'auto', cls: 'c-dist' },
  { k: 'region', t: 'Region', w: '80px', cls: 'c-reg' },
  { k: 'units', t: 'Units', w: '112px', num: true },
  { k: 'pm', t: 'Property Manager', w: 'auto', cls: 'c-pm' },
  { k: 'status', t: 'Status', w: '96px' },
];

export function Portfolio() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { data, loading, error, reload } = usePortfolio();

  // Memoized on the query string. Without this, `readFilters` hands back a new
  // object on every render, `list`'s useMemo never hits, and every child that
  // depends on the filtered array sees a brand-new reference each render — which
  // is what was throwing away the map's zoom whenever anything above re-rendered.
  const parsed = useMemo(() => readFilters(params), [params.toString()]);

  const facilities = data?.facilities ?? [];
  const units = data?.units ?? {};
  const summary = data?.summary;

  const regions = useMemo(
    () => ['All', ...new Set(facilities.map((f) => f.region).filter(Boolean) as string[])],
    [facilities],
  );
  const brands = useMemo(
    () => ['All', ...new Set(facilities.map((f) => f.brand).filter(Boolean) as string[])],
    [facilities],
  );
  // Built from the data actually present, and SORTED — there are ~30 states and
  // ~40 district managers, which is past the point where source order is
  // findable. Dirty values (one row's State column holds a city) show up as
  // their own option instead of silently filtering to nothing.
  const states = useMemo(
    () =>
      [
        'All',
        ...[...new Set(facilities.map((f) => f.state).filter(Boolean) as string[])].sort((a, b) =>
          a.localeCompare(b),
        ),
      ],
    [facilities],
  );
  // The District column holds the district MANAGER's name. Sites with no DM are
  // offered explicitly rather than being invisible — "who has no district
  // manager" is one of the stat tiles, so it has to be reachable here too.
  const districts = useMemo(() => {
    const named = [...new Set(facilities.map((f) => f.district).filter(Boolean) as string[])].sort(
      (a, b) => a.localeCompare(b),
    );
    const anyUnassigned = facilities.some((f) => !f.district);
    return ['All', ...named, ...(anyUnassigned ? ['—'] : [])];
  }, [facilities]);

  /**
   * A filter value the options no longer contain is DROPPED, not honoured.
   *
   * The selects are built from the loaded data, so a stale URL — a shared link
   * naming a district manager who has since left — leaves the control showing
   * its first option, "All district managers", while the filter still excludes
   * every row. The screen would then claim no filter is applied over an empty list.
   * Only applied once the data is in; before that, nothing is knowable.
   */
  const filters = useMemo<FilterState>(() => {
    if (!facilities.length) return parsed;
    return {
      ...parsed,
      state: states.includes(parsed.state) ? parsed.state : 'All',
      district: districts.includes(parsed.district) ? parsed.district : 'All',
      region: regions.includes(parsed.region) ? parsed.region : 'All',
      brand: brands.includes(parsed.brand) ? parsed.brand : 'All',
    };
  }, [parsed, facilities.length, states, districts, regions, brands]);

  const set = (patch: Partial<FilterState>) =>
    setParams(writeFilters({ ...filters, ...patch }), { replace: true });

  const list = useMemo(() => applyFilters(facilities, units, filters), [facilities, units, filters]);

  const maxUnits = useMemo(
    () => Math.max(1, ...facilities.map((f) => units[f.id] ?? 0)),
    [facilities, units],
  );

  const archiveMode = filters.status === 'Inactive';

  const open = (f: Facility) => navigate(`/facility/${encodeURIComponent(f.id)}?${params.toString()}`);

  const sortOn = (k: SortKey) => set({ sort: k, dir: filters.sort === k ? ((filters.dir * -1) as 1 | -1) : 1 });

  if (error) {
    return (
      <StateMessage
        title="Facility data is unavailable"
        body={
          error.status === 403
            ? 'Your account does not have access to FacilityWISE. Ask IT to grant the FacilityWISE viewer role in WISE-IAM.'
            : 'The facility master on WPICSQL2502 could not be read. Nothing is shown rather than an empty portfolio, which would read as "no facilities".'
        }
        detail={error.detail ?? error.message}
        onRetry={reload}
      />
    );
  }

  return (
    <>
      <div className="pf-head">
        <div className="pf-in">
          <div className="pf-top">
            <div>
              <h1>Portfolio</h1>
              <div className="pf-sub">
                {summary ? (
                  <>
                    <b>{summary.managed}</b> managed facilities · <b>{summary.totalUnits.toLocaleString()}</b>{' '}
                    units · <b>{summary.archived}</b> in archive
                  </>
                ) : (
                  'Loading the facility master…'
                )}
              </div>
            </div>
          </div>

          <div className="pf-stats">
            <button
              className={`pstat${filters.status === 'Active' ? ' on' : ''}`}
              onClick={() => set({ status: 'Active' })}
              type="button"
            >
              <span className="ps-v">{summary?.managed ?? '—'}</span>
              <span className="ps-l">Managed</span>
            </button>
            <button
              className={`pstat warn${filters.status === 'Vacancy' ? ' on' : ''}`}
              onClick={() => set({ status: 'Vacancy' })}
              type="button"
              // Null means the staff list was not read, not that nobody is hiring.
              title={summary && summary.vacancies === null ? 'Facility Staff could not be read' : undefined}
            >
              <span className="ps-v">{summary?.vacancies ?? '—'}</span>
              <span className="ps-l">Manager vacancy</span>
            </button>
            <button
              className={`pstat warn${filters.status === 'Unmanned' ? ' on' : ''}`}
              onClick={() => set({ status: 'Unmanned' })}
              type="button"
            >
              <span className="ps-v">{summary?.unmanned ?? '—'}</span>
              <span className="ps-l">Unmanned</span>
            </button>
            {/* Display only — there is no useful list behind it, so it is not a
                button. A tile that looks clickable and is not is worse than a
                tile that plainly is not. */}
            <div className="pstat flat">
              <span className="ps-v">{summary?.noDistrictManager ?? '—'}</span>
              <span className="ps-l">No district manager</span>
            </div>
            <span className="pf-div" />
            <button
              className={`pstat arch${filters.status === 'Inactive' ? ' on' : ''}`}
              onClick={() => set({ status: 'Inactive' })}
              type="button"
            >
              <span className="ps-v">{summary?.archived ?? '—'}</span>
              <span className="ps-l">Archive</span>
            </button>
            <button
              className={`pstat arch${filters.status === 'All' ? ' on' : ''}`}
              onClick={() => set({ status: 'All' })}
              type="button"
            >
              <span className="ps-v">{summary?.total ?? '—'}</span>
              <span className="ps-l">Everything</span>
            </button>
          </div>

          <div className="pf-tools">
            <div className="vtog">
              <button
                className={`vbtn${filters.view === 'table' ? ' on' : ''}`}
                onClick={() => set({ view: 'table' })}
                type="button"
              >
                Table
              </button>
              <button
                className={`vbtn${filters.view === 'map' ? ' on' : ''}`}
                onClick={() => set({ view: 'map' })}
                type="button"
              >
                Map
              </button>
            </div>
          </div>
        </div>
      </div>

      {archiveMode && (
        <div className="archbar">
          Viewing the archive — {summary?.archived ?? 0} facilities Westport no longer manages. Retained
          for reference; excluded from every operational count above.
          <button className="archback" onClick={() => set({ status: 'Active' })} type="button">
            Back to managed →
          </button>
        </div>
      )}

      <div className="pf-toolbar">
        <div className="searchbox wide">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            type="text"
            value={filters.q}
            onChange={(e) => set({ q: e.target.value })}
            placeholder="Search name, code, city, entity, manager…"
            aria-label="Search facilities"
          />
        </div>

        <select
          value={filters.region}
          onChange={(e) => set({ region: e.target.value })}
          aria-label="Region"
        >
          {regions.map((r) => (
            <option key={r} value={r}>
              {r === 'All' ? 'All regions' : r}
            </option>
          ))}
        </select>

        <select value={filters.brand} onChange={(e) => set({ brand: e.target.value })} aria-label="Brand">
          {brands.map((b) => (
            <option key={b} value={b}>
              {b === 'All' ? 'All brands' : b}
            </option>
          ))}
        </select>

        <select value={filters.state} onChange={(e) => set({ state: e.target.value })} aria-label="State">
          {states.map((st) => (
            <option key={st} value={st}>
              {st === 'All' ? 'All states' : st}
            </option>
          ))}
        </select>

        {/* The three names for this control disagreed: the aria-label already
            said "District manager" and the empty option "No district manager",
            while the default option said "All districts" — so a screen reader
            and the screen described different things, and the visible one
            described a place rather than the person the column actually holds.
            All three now say the same thing. Change them together. */}
        <select
          value={filters.district}
          onChange={(e) => set({ district: e.target.value })}
          aria-label="District manager"
        >
          {districts.map((d) => (
            <option key={d} value={d}>
              {d === 'All' ? 'All district managers' : d === '—' ? 'No district manager' : d}
            </option>
          ))}
        </select>

        {/* The brand colour key that used to sit here is GONE. It listed every
            brand in the portfolio as a row of coloured chips — twelve of them,
            spilling off the right edge — to explain an accent bar nobody was
            reading as a legend anyway. The Brand column and the "All brands"
            filter next to it already name the brand in words, which is the
            thing people were actually using. The accent bar and the Brand
            column dot keep their colour; only the legend is removed. */}

        <span className="pf-count">{loading ? 'Loading' : `${list.length} shown`}</span>
      </div>

      {filters.view === 'table' ? (
        <div className="pf-tblwrap">
          <table className="pf-tbl">
            <thead>
              <tr>
                {COLS.map((c) => (
                  <th
                    key={c.k}
                    style={{ width: c.w }}
                    className={`${c.cls ? `${c.cls} ` : ''}${c.num ? 'num ' : ''}sortable${
                      filters.sort === c.k ? ' on' : ''
                    }`}
                    onClick={() => sortOn(c.k)}
                    aria-sort={
                      filters.sort === c.k ? (filters.dir > 0 ? 'ascending' : 'descending') : 'none'
                    }
                  >
                    {c.t}
                    <span className="sarr">{filters.sort === c.k ? (filters.dir > 0 ? '↑' : '↓') : ''}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 12 }, (_, i) => (
                    <tr key={i}>
                      {COLS.map((c) => (
                        <td key={c.k} className={c.cls}>
                          <div className="fw-skel" style={{ width: c.k === 'name' ? '70%' : '55%' }} />
                        </td>
                      ))}
                    </tr>
                  ))
                : list.map((f) => {
                    const u = units[f.id] ?? null;
                    return (
                      <tr
                        className={`prow${f.active ? '' : ' arch'}`}
                        key={f.id}
                        onClick={() => open(f)}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            open(f);
                          }
                        }}
                      >
                        <td
                          className="c-code"
                          style={{
                            boxShadow: `inset 3px 0 0 ${f.active ? brandColor(f.brand) : 'var(--ink-200)'}`,
                          }}
                          title={f.brand ?? undefined}
                        >
                          <span className={`lcode${f.active ? '' : ' off'}`}>{f.code}</span>
                        </td>
                        <td className="c-name">
                          <div className="pname">{f.name}</div>
                          <div className="psub">
                            {[f.street, f.city, f.state].filter(Boolean).join(', ')} {f.zip ?? ''}
                          </div>
                        </td>
                        <td className="pdim c-brand">
                          <span className="bdot" style={{ background: brandColor(f.brand) }} />
                          {f.brand ?? <span className="na">—</span>}
                        </td>
                        <td className="pdim c-dist">
                          {f.district ? (
                            f.district
                          ) : f.active ? (
                            <span className="warn-t">Unassigned</span>
                          ) : (
                            <span className="na">—</span>
                          )}
                        </td>
                        <td className="pdim c-reg">{f.region ?? <span className="na">—</span>}</td>
                        <td className="num">
                          {u ? (
                            <span className="ubar">
                              <span className="ubar-t">
                                <span className="ubar-f" style={{ width: `${Math.round((u / maxUnits) * 100)}%` }} />
                              </span>
                              <span className="mono">{u.toLocaleString()}</span>
                            </span>
                          ) : (
                            <span className="na">—</span>
                          )}
                        </td>
                        <td className="pdim c-pm">
                          {/* The grey dash is "present, nothing recorded" only. A
                              missing row, a conflicting pair and a failed read
                              each say so — never the same dash. */}
                          {f.team.state !== 'present' ? (
                            <span className="na" title={f.team.detail}>
                              {teamUnavailable(f.team)}
                            </span>
                          ) : f.pm === 'Now Hiring' ? (
                            f.active ? (
                              <span className="warn-t">Now Hiring</span>
                            ) : (
                              <span className="na">Now Hiring</span>
                            )
                          ) : (
                            f.pm ?? <span className="na">—</span>
                          )}
                        </td>
                        <td>
                          {f.active ? (
                            <span className="chip ok sm">Active</span>
                          ) : (
                            <span className="chip off sm">
                              {f.mgmt === 'No Longer Managed' ? 'No longer managed' : 'Inactive'}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>

          {!loading && list.length === 0 && (
            <div className="unloaded" style={{ padding: '40px 24px' }}>
              <b>No facilities match</b>
              <span>
                {filters.status === 'Vacancy' && summary?.vacancies === null
                  ? 'Vacancies cannot be listed: Facility Staff could not be read on the last refresh.'
                  : 'Clear the search or widen the filters.'}
              </span>
            </div>
          )}
        </div>
      ) : (
        <Suspense fallback={<div className="mapwrap"><div className="map-empty">Loading map…</div></div>}>
          <PortfolioMap facilities={list} units={units} onOpen={open} />
        </Suspense>
      )}

      <div className="fw-sr" role="status" aria-live="polite">
        {loading ? 'Loading facilities' : `${list.length} facilities shown`}
      </div>
    </>
  );
}
